import React, { useState, useEffect } from 'react';
import { Form, Select, message, Typography, Spin, InputNumber, Button, Table, Input } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;

interface SectorData {
  id: number;
  descripcion: string;
}

interface EquipoData {
  id: number;
  descripcion: string;
  id_sector: number;
}

interface MaquinariaData {
  id: number;
  descripcion: string;
  id_equipo: number;
}

interface InsumoData {
  id: number;
  descripcion: string;
  precio_promedio: number;
}

interface TablaEgresoData {
  key: number;
  insumo_id: number;
  insumo: string;
  cantidad: number;
  precio: number;
  subtotal: number;
}

interface PadronData {
  id: number;
  razon: string;
}

const EditarEgresos: React.FC = () => {
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const { id } = useParams<{ id: string }>();

  const [sectores, setSectores] = useState<SectorData[]>([]);
  const [equipos, setEquipos] = useState<EquipoData[]>([]);
  const [maquinarias, setMaquinarias] = useState<MaquinariaData[]>([]);
  const [insumos, setInsumos] = useState<InsumoData[]>([]);
  const [loadingInsumos, setLoadingInsumos] = useState<boolean>(false);
  const [selectedSector, setSelectedSector] = useState<number>();
  const [selectedEquipo, setSelectedEquipo] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [cantidad, setCantidad] = useState<number>(0);
  const [datosTabla, setDatosTabla] = useState<TablaEgresoData[]>([]);
  const [padrones, setPadrones] = useState<PadronData[]>([]);
  const [loadingPadrones, setLoadingPadrones] = useState<boolean>(false);
  const [enviandoDatos, setEnviandoDatos] = useState<boolean>(false);
  const [stockActual, setStockActual] = useState<number>(0);
  const navigate = useNavigate();
  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  // Cargar sectores
  const fetchSectores = async () => {
    const messageKey = 'loadingSectores';
    try {
      setLoading(true);
      message.loading({ content: 'Cargando Sectores...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_sectores/${empresa}/${sucursal}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar sectores');
      }

      const data = await response.json();
      setSectores(data);
      message.success({ content: 'Sectores cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      message.error({ content: 'Error al cargar los sectores', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Cargar equipos basados en el sector seleccionado
  const fetchEquipos = async (sectorId: number) => {
    const messageKey = 'loadingEquipos';
    try {
      setLoading(true);
      message.loading({ content: 'Cargando Equipos...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_equipos_sectores/${empresa}/${sucursal}/${sectorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar equipos');
      }

      const data = await response.json();
      setEquipos(data);
      message.success({ content: 'Equipos cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      message.error({ content: 'Error al cargar los equipos', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Cargar maquinarias basadas en el equipo seleccionado
  const fetchMaquinarias = async (equipoId: number, sectorId: number) => {
    const messageKey = 'loadingMaquinarias';
    try {
      if (!sectorId || !equipoId) {
        console.log('No se pueden cargar maquinarias - Faltan datos:', {
          sector: sectorId,
          equipo: equipoId
        });
        return;
      }

      setLoading(true);
      message.loading({ content: 'Cargando Maquinarias...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_INSUMOS_URL}/listar_maquinaria_equipos_sectores/${empresa}/${sucursal}/${sectorId}/${equipoId}`;

      console.log('Realizando petición a:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar maquinarias');
      }

      const data = await response.json();
      console.log('Maquinarias recibidas:', data);
      setMaquinarias(data);
      message.success({ content: 'Maquinarias cargadas correctamente', key: messageKey });
    } catch (error) {
      console.error('Error al cargar maquinarias:', error);
      message.error({ content: 'Error al cargar las maquinarias', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar insumos
  const fetchInsumos = async () => {
    const messageKey = 'loadingInsumos';
    try {
      setLoadingInsumos(true);
      message.loading({ content: 'Cargando Insumos...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar insumos');
      }

      const data = await response.json();
      setInsumos(data);
      message.success({ content: 'Insumos cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      message.error({ content: 'Error al cargar los insumos', key: messageKey });
    } finally {
      setLoadingInsumos(false);
    }
  };

  // Cargar padrones
  const fetchPadrones = async () => {
    try {
      setLoadingPadrones(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar retiros');
      }

      const data = await response.json();
      console.log('padrones', data);
      setPadrones(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los retiros');
    } finally {
      setLoadingPadrones(false);
    }
  };

  // Cargar sectores al montar el componente
  useEffect(() => {
    if (empresa && id) {
      const loadAllData = async () => {
        try {
          setLoading(true);
          setIsLoadingComplete(false);

          // 1. Primero cargamos los sectores
          await fetchSectores();

          // 2. Luego cargamos los datos del egreso
          const egresoData = await fetchEgresoData();

          if (egresoData) {
            // 3. Primero establecemos el sector
            setSelectedSector(egresoData.comprobante.sector_id);

            // 4. Cargamos equipos basados en el sector del egreso
            await fetchEquipos(egresoData.comprobante.sector_id);

            // 5. Establecemos el equipo
            setSelectedEquipo(egresoData.comprobante.equipo_id);

            // 6. Ahora cargamos maquinarias con ambos valores establecidos
            await fetchMaquinarias(
              egresoData.comprobante.equipo_id,
              egresoData.comprobante.sector_id
            );

            // 7. Establecemos los valores del formulario
            form.setFieldsValue({
              sector: egresoData.comprobante.sector_id,
              equipo: egresoData.comprobante.equipo_id,
              maquinaria: egresoData.comprobante.motor_id,
              retiro: egresoData.comprobante.retiro_id,
              observaciones: egresoData.comprobante.observacion
            });
          }

          // 8. Cargamos datos independientes en paralelo
          await Promise.all([
            fetchInsumos(),
            fetchPadrones()
          ]);

          setIsLoadingComplete(true);
        } catch (error) {
          console.error('Error loading data:', error);
          message.error('Error al cargar los datos');
        } finally {
          setLoading(false);
        }
      };

      loadAllData();
    }
  }, [empresa, id]);

  // Manejadores de cambios
  const handleSectorChange = (value: number) => {
    console.log('Sector seleccionado:', value);
    setSelectedSector(value);
    setSelectedEquipo(undefined);
    setMaquinarias([]);
    form.setFieldsValue({
      equipo: undefined,
      maquinaria: undefined
    });

    if (value) {
      fetchEquipos(value);
    } else {
      setEquipos([]);
    }
  };

  const handleEquipoChange = (value: number) => {
    console.log('handleEquipoChange - Equipo seleccionado:', value);
    console.log('handleEquipoChange - Sector actual:', selectedSector);

    // Limpiar maquinaria
    setMaquinarias([]);
    form.setFieldsValue({ maquinaria: undefined });

    // Actualizar el equipo seleccionado
    setSelectedEquipo(value);

    // Solo cargar maquinarias si tenemos sector
    if (value && selectedSector) {
      console.log('Cargando maquinarias para sector:', selectedSector, 'y equipo:', value);
      fetchMaquinarias(value, selectedSector);
    }
  };

  // Modificar el manejador de cambio de maquinaria para cargar insumos
  //const handleMaquinariaChange = (value: number) => {
  //if (value) {
  //  fetchInsumos();
  //  fetchPadrones();
  //  fetchEgresoData();
  //}
  //};

  // Manejadores para los nuevos campos
  const handleCantidadChange = (value: number | null) => {
    const newCantidad = value || 0;
    setCantidad(newCantidad);
    form.setFieldsValue({
      cantidad: newCantidad
    });
  };

  // Función para agregar datos a la tabla
  const agregarATabla = () => {
    const insumoSeleccionado = insumos.find(i => i.id === form.getFieldValue('insumo'));

    if (!insumoSeleccionado) {
      message.error('Por favor seleccione un insumo');
      return;
    }

    const cantidad = form.getFieldValue('cantidad') || 0;
    const precio = insumoSeleccionado.precio_promedio || 0;
    const subtotal = cantidad * precio;

    const nuevoRegistro: TablaEgresoData = {
      key: Date.now(),
      insumo_id: insumoSeleccionado.id,
      insumo: insumoSeleccionado.descripcion,
      cantidad,
      precio,
      subtotal
    };

    setDatosTabla([...datosTabla, nuevoRegistro]);

    // Clear form fields
    form.setFieldsValue({
      insumo: undefined,
      cantidad: null
    });

    // Reset cantidad state
    setCantidad(0);
  };

  // Función para eliminar un registro
  const eliminarRegistro = (key: number) => {
    setDatosTabla(datosTabla.filter(item => item.key !== key));
  };

  // Columnas de la tabla con estilo en los encabezados
  const columns = [
    {
      title: 'Insumo',
      dataIndex: 'insumo',
      key: 'insumo',
      className: 'header-column', // Agregamos una clase para el estilo
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right' as const,
      className: 'header-column',
    },
    {
      title: 'Acción',
      key: 'accion',
      align: 'center' as const,
      className: 'header-column',
      render: (_: any, record: TablaEgresoData) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => eliminarRegistro(record.key)}
        />
      ),
    },
  ];

  // Función para enviar los datos
  const finalizarEgreso = async () => {
    const messageKey = 'finalizandoEgreso';
    try {
      // Validar que haya datos en la tabla
      if (datosTabla.length === 0) {
        message.error('Debe agregar al menos un insumo');
        return;
      }

      // Validar campos requeridos
      const values = await form.validateFields();

      setEnviandoDatos(true);
      message.loading({ content: 'Actualizando egreso...', key: messageKey, duration: 0 });

      const datosEgreso = {
        empresa,
        sucursal,
        sector: values.sector,
        equipo: values.equipo,
        maquinaria: values.maquinaria,
        retiro: values.retiro,
        observaciones: values.observaciones || '',
        items: datosTabla.map(item => ({
          insumo_id: item.insumo_id,
          cantidad: item.cantidad,
          precio: item.precio
        }))
      };

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/editar_egreso/${empresa}/${sucursal}/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datosEgreso)
      });

      if (!response.ok) {
        throw new Error('Error al guardar el egreso');
      }

      message.success({ content: 'Egreso guardado correctamente', key: messageKey });

      // Reiniciar formulario después de guardar
      //form.resetFields();
      //setSelectedSector(undefined);
      //setSelectedEquipo(undefined);
      //setEquipos([]);
      //setMaquinarias([]);
      //setInsumos([]);
      //setCantidad(0);
      //setPrecioPromedio(0);
      //setSubtotal(0);
      //setDatosTabla([]);

    } catch (error) {
      console.error('Error:', error);
      message.error({
        content: error instanceof Error ? error.message : 'Error al guardar el egreso',
        key: messageKey
      });
    } finally {
      setEnviandoDatos(false);
      navigate('/listar_egresos');
    }
  };

  // Función para obtener el stock del insumo
  const fetchStockInsumo = async (insumoId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/stock_insumos/${empresa}/${sucursal}/${insumoId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener el stock');
      }

      const stock = await response.json();
      setStockActual(stock);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al obtener el stock del insumo');
    }
  };

  // Modificar el handleInsumoChange para incluir la llamada al precio promedio
  const handleInsumoChange = async (value: number) => {
    if (value) {
      await fetchStockInsumo(value);

      // Resetear cantidad
      const newCantidad = 0;
      form.setFieldsValue({
        cantidad: newCantidad
      });

      setCantidad(newCantidad);
    } else {
      setStockActual(0);
      form.setFieldsValue({
        cantidad: 0
      });
    }
  };

  // Modificar fetchEgresoData para mantener los valores originales
  const fetchEgresoData = async () => {
    const messageKey = 'loadingEgreso';
    try {
      message.loading({ content: 'Cargando datos del egreso...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/editar_egreso/${empresa}/${sucursal}/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar datos del egreso');
      }

      const data = await response.json();
      console.log('Datos del egreso:', data);

      // Mantener los valores originales de precio y subtotal
      const tableData = data.movimientos.map((item: any) => ({
        key: Date.now() + Math.random(),
        insumo_id: item.insumo_id,
        insumo: item.insumo,
        cantidad: item.cantidad,
        precio: item.precio_unitario || item.precio, // Mantener el precio original
        subtotal: item.subtotal // Mantener el subtotal original
      }));
      setDatosTabla(tableData);

      message.success({ content: 'Datos del egreso cargados correctamente', key: messageKey });
      return data;
    } catch (error) {
      console.error('Error:', error);
      message.error({ content: 'Error al cargar los datos del egreso', key: messageKey });
      return null;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
        Editar Egresos
      </Title>

      <Form
        form={form}
        layout="horizontal"
        style={{ maxWidth: '1200px', margin: '0 auto' }}
      >
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '20px' }}>
          {/* Selector de Sección */}
          <Form.Item
            label="Sección"
            name="sector"
            rules={[{ required: true, message: 'Por favor seleccione una sección' }]}
            style={{ flex: 1, minWidth: '200px' }}
          >
            <Select
              placeholder="Seleccione una sección"
              onChange={handleSectorChange}
              loading={loading}
              showSearch
              optionFilterProp="children"
            >
              {sectores.map(sector => (
                <Option key={sector.id} value={sector.id}>
                  {sector.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Selector de Equipo */}
          <Form.Item
            label="Equipo"
            name="equipo"
            rules={[{ required: true, message: 'Por favor seleccione un equipo' }]}
            style={{ flex: 1, minWidth: '200px' }}
          >
            <Select
              placeholder="Seleccione un equipo"
              onChange={handleEquipoChange}
              loading={loading}
              disabled={!selectedSector}
              showSearch
              optionFilterProp="children"
            >
              {equipos.map(equipo => (
                <Option key={equipo.id} value={equipo.id}>
                  {equipo.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Selector de Maquinaria */}
          <Form.Item
            label="Maquinaria"
            name="maquinaria"
            rules={[{ required: true, message: 'Por favor seleccione una maquinaria' }]}
            style={{ flex: 1, minWidth: '200px' }}
          >
            <Select
              placeholder="Seleccione una maquinaria"
              loading={loading}
              disabled={!selectedEquipo}
              showSearch
              optionFilterProp="children"
            //onChange={handleMaquinariaChange}
            >
              {maquinarias.map(maquinaria => (
                <Option key={maquinaria.id} value={maquinaria.id}>
                  {maquinaria.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Título centrado */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <div style={{ flex: 1 }} />
          <Title level={3} style={{ margin: 0, flex: 1, textAlign: 'center' }}>
            Insumos
          </Title>
          <div style={{ flex: 1 }} />
        </div>

        {/* Contenedor principal para insumos */}
        <div style={{ marginBottom: '20px' }}>
          {/* Fila superior con Insumo, Cantidad, Stock y Botón */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            marginBottom: '20px'
          }}>
            {/* Select de Insumo */}
            <Form.Item
              label="Insumo"
              name="insumo"
              style={{ flex: '1 1 auto', marginBottom: 0 }}
            >
              <Select
                placeholder="Seleccione un insumo"
                showSearch
                optionFilterProp="children"
                loading={loadingInsumos}
                disabled={!isLoadingComplete}
                onChange={handleInsumoChange}
              >
                {insumos.map(insumo => (
                  <Option key={insumo.id} value={insumo.id}>
                    {insumo.descripcion}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Input de Cantidad */}
            <Form.Item
              label="Cantidad"
              name="cantidad"
              style={{ width: '220px', marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                onChange={handleCantidadChange}
                placeholder="0.00"
              />
            </Form.Item>

            {/* Stock al mismo nivel */}
            <Form.Item
              label="Stock"
              style={{ width: '220px', marginBottom: 0 }}
            >
              <InputNumber
                style={{
                  width: '100%',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  textAlign: 'right'
                }}
                disabled
                value={stockActual}
                precision={2}
                formatter={(value) => (value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Form.Item>

            {/* Botón Agregar */}
            <Form.Item style={{ marginBottom: 0, alignSelf: 'flex-end' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={agregarATabla}
                disabled={!form.getFieldValue('insumo') || !cantidad}
              >
                Agregar
              </Button>
            </Form.Item>
          </div>

          {/* Tabla de insumos centrada */}
          <div style={{
            width: '100%',
            maxWidth: '1000px', // Ajusta este valor según necesites
            margin: '0 auto'    // Esto centra la tabla
          }}>
            <Table
              columns={columns}
              dataSource={datosTabla}
              pagination={false}
              bordered
              scroll={{ y: 200 }}
              className="custom-table"
              summary={pageData => {
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>Total Items</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>{pageData.length}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </div>
        </div>

        {/* Campos adicionales debajo de la tabla */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginTop: '20px',
          alignItems: 'flex-start'
        }}>
          {/* Select de Retiro */}
          <Form.Item
            label="Retiro"
            name="retiro"
            rules={[{ required: true, message: 'Por favor seleccione el retiro' }]}
            style={{ width: '300px' }}
          >
            <Select
              placeholder="Seleccione retiro"
              showSearch
              optionFilterProp="children"
              loading={loadingPadrones}
            >
              {padrones.map(padron => (
                <Option key={padron.id} value={padron.id}>
                  {padron.razon}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Input de Observaciones */}
          <Form.Item
            label="Observaciones"
            name="observaciones"
            style={{ flex: 1 }}
          >
            <Input.TextArea
              placeholder="Ingrese observaciones"
              rows={1}
            />
          </Form.Item>
        </div>

        {/* Botones de acción */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '20px',
            marginBottom: '20px'
          }}
        >
          <Button
            type="primary"
            size="large"
            onClick={finalizarEgreso}
            loading={enviandoDatos}
            disabled={enviandoDatos}
          >
            ActualizarEgreso
          </Button>
          <Button
            size="large"
            onClick={() => {
              // Reiniciar formulario
              form.resetFields();
              // Reiniciar estados
              setSelectedSector(undefined);
              setSelectedEquipo(undefined);
              setEquipos([]);
              setMaquinarias([]);
              setInsumos([]);
              setCantidad(0);
            }}
          >
            Cancelar
          </Button>
          <Button
            size="large"
            onClick={() => {
              // Lógica para ir al listado
              navigate('/listar_egresos');
            }}
          >
            Listado
          </Button>
        </div>

        {/* Agregar estilos CSS */}
        <style>
          {`
            .custom-table .ant-table-thead > tr > th {
              background-color: #f5f5f5 !important;
              font-weight: bold;
            }
            
            .custom-table .ant-table-thead > tr > th.ant-table-cell {
              background-color: #f5f5f5 !important;
            }
            
            .custom-table .ant-table-thead > tr:hover > th {
              background-color: #f0f0f0 !important;
            }
            
            .stock-input .ant-input-number-input {
              text-align: right !important;
            }
          `}
        </style>
      </Form>
    </div>
  );
};

export default EditarEgresos;